import { useRef, useState } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { TextField } from 'Components/ui';

const INK = 'var(--vo-ink)';
const MUTED = 'var(--vo-slate)';
const BLUE = 'var(--color-brand)';
const BORDER = 'var(--vo-steel)';
const TEXT = 'var(--vo-ink-2)';
const DIVIDER = 'var(--vo-cloud)';

const Card = styled.div`
  max-width: 800px;
  background: white;
  border-radius: 14px;
  padding: 28px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
`;
const SectionHead = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid ${DIVIDER};
`;
const IconTile = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: linear-gradient(135deg, var(--vo-capri), var(--vo-cobalto));
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;
const SectionTitle = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: ${INK};
  margin: 0;
`;
const SectionSub = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: ${MUTED};
  margin: 0;
`;
const FieldsWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;
const FieldWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;
const Label = styled.label`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: ${TEXT};
`;
const ErrorMsg = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: var(--vo-danger);
  margin: 0;
`;
const ChipInputWrap = styled.div`
  min-height: 40px;
  border: 1.5px solid ${BORDER};
  border-radius: 10px;
  padding: 8px 12px;
  background: white;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
  box-sizing: border-box;
  &:focus-within { border-color: ${BLUE}; }
`;
const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: var(--vo-capri-50);
  color: var(--vo-celeste);
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 500;
  padding: 3px 10px;
  border-radius: 20px;
  cursor: pointer;
`;
const ChipXBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  color: var(--vo-celeste);
`;
const ChipInner = styled.input`
  border: none;
  outline: none;
  background: transparent;
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: ${INK};
  flex: 1;
  min-width: 100px;
  &::placeholder { color: ${MUTED}; }
`;

const DatabaseSvg = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <ellipse cx="12" cy="5" rx="9" ry="3"/>
    <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/>
    <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/>
  </svg>
);
const XSvg = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

const TagInput = ({ value, onChange, placeholder }) => {
  const [inputVal, setInputVal] = useState('');
  const inputRef = useRef(null);
  const tags = Array.isArray(value) ? value : [];

  const addTag = () => {
    const trimmed = inputVal.trim().replace(/,$/, '');
    if (trimmed && !tags.includes(trimmed)) onChange([...tags, trimmed]);
    setInputVal('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addTag(); }
    if (e.key === 'Backspace' && inputVal === '' && tags.length > 0) onChange(tags.slice(0, -1));
  };

  return (
    <ChipInputWrap onClick={() => inputRef.current?.focus()}>
      {tags.map(tag => (
        <Chip key={tag}>
          {tag}
          <ChipXBtn type="button" onClick={e => { e.preventDefault(); onChange(tags.filter(t => t !== tag)); }}>
            <XSvg />
          </ChipXBtn>
        </Chip>
      ))}
      <ChipInner
        ref={inputRef}
        value={inputVal}
        onChange={e => setInputVal(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={tags.length === 0 ? placeholder : ''}
      />
    </ChipInputWrap>
  );
};

export const Detalhes = ({ register, control, errors, id }) => {
  const { t } = useTranslation(['mapping']);

  return (
    <Card>
      <SectionHead>
        <IconTile><DatabaseSvg /></IconTile>
        <div>
          <SectionTitle>{t('mapping:dataTypes.form.sectionTitle')}</SectionTitle>
          <SectionSub>{t('mapping:dataTypes.form.sectionSubtitle')}</SectionSub>
        </div>
      </SectionHead>
      <FieldsWrap>
        <FieldWrap data-tour="data-name">
          <Label>{t('mapping:dataTypes.form.name.label')} *</Label>
          <TextField
            fullWidth
            {...register('name', { required: t('mapping:dataTypes.form.name.required') })}
            placeholder={t('mapping:dataTypes.form.name.placeholder')}
            error={!!errors?.name}
            disabled={!!id}
            autoFocus={!id}
          />
          {errors?.name && <ErrorMsg>{errors.name.message}</ErrorMsg>}
        </FieldWrap>

        <FieldWrap data-tour="data-tags">
          <Label>{t('mapping:dataTypes.form.tags.label')}</Label>
          <Controller
            control={control}
            name="tags"
            render={({ field: { onChange, value } }) => (
              <TagInput
                value={value}
                onChange={onChange}
                placeholder={t('mapping:dataTypes.form.tags.placeholder')}
              />
            )}
          />
        </FieldWrap>

        <FieldWrap data-tour="data-description">
          <Label>{t('mapping:dataTypes.form.description.label')}</Label>
          <TextField
            multiline
            fullWidth
            {...register('description')}
            rows={4}
            placeholder={t('mapping:dataTypes.form.description.placeholder')}
          />
        </FieldWrap>
      </FieldsWrap>
    </Card>
  );
};
